/**
 * Council Trail Task 6.5 — WakeBriefBanner.
 *
 * Banner that lands at the surface root when wake_brief_service returned
 * a digest of activity the operator missed. Collapsed-by-default; the
 * expand chevron reveals the per-persona breakdown. Action buttons:
 * Review (opens activity drawer), Approve all (bulk-approves pending
 * proposals — v0.1 stub), Dismiss (× — marks read).
 *
 * Cost-cap-hit warning: when brief.digest.cost_cap_hit is true, the
 * banner shifts from amber to red and surfaces a warning chip.
 */
import React, { useState } from 'react';

import type { WakeBriefDigest, WakeBriefPayload } from '../../types/council';

export interface WakeBriefBannerProps {
  brief: WakeBriefPayload;
  onApproveAll: () => void;
  onDismiss: () => void;
  onReviewActivity: () => void;
}

export function WakeBriefBanner({
  brief,
  onApproveAll,
  onDismiss,
  onReviewActivity,
}: WakeBriefBannerProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const capHit = brief.digest.cost_cap_hit;

  const palette = capHit
    ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100'
    : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100';

  return (
    <div
      data-testid="wake-brief-banner"
      className={`min-h-0 border-b ${palette} px-4 py-2 text-sm`}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span
          data-testid="wake-brief-summary"
          className="truncate min-w-0 flex-1"
        >
          ✦ While you were away: {brief.digest.totals.turns} turns,{' '}
          {brief.digest.totals.artifacts} artifacts,{' '}
          ${brief.digest.totals.cost_used_usd}
          {capHit && (
            <span
              data-testid="wake-brief-cap-hit"
              className="ml-2 inline-flex items-center rounded
                         bg-red-200 dark:bg-red-900 text-red-900 dark:text-red-100
                         px-1.5 py-0.5 text-xs font-medium"
            >
              cost cap hit
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            data-testid="wake-brief-expand"
            onClick={() => setExpanded(v => !v)}
            aria-expanded={expanded}
            className="text-xs px-1.5 py-0.5 rounded
                       hover:bg-amber-100 dark:hover:bg-amber-900"
          >
            {expanded ? '▲' : '▼'}
          </button>
          <button
            data-testid="wake-brief-review"
            onClick={onReviewActivity}
            className="text-xs px-2 py-0.5 rounded
                       hover:bg-amber-100 dark:hover:bg-amber-900"
          >
            Review
          </button>
          <button
            data-testid="wake-brief-approve-all"
            onClick={onApproveAll}
            className="text-xs px-2 py-0.5 rounded
                       hover:bg-amber-100 dark:hover:bg-amber-900"
          >
            Approve all
          </button>
          <button
            data-testid="wake-brief-dismiss"
            onClick={onDismiss}
            aria-label="Dismiss wake brief"
            className="text-xs px-1.5 py-0.5 rounded
                       hover:bg-amber-100 dark:hover:bg-amber-900"
          >
            ×
          </button>
        </div>
      </div>
      {expanded && <WakeBriefDetail digest={brief.digest} />}
    </div>
  );
}

function WakeBriefDetail({ digest }: { digest: WakeBriefDigest }) {
  const personaEntries = Object.entries(digest.by_persona);
  return (
    <div
      data-testid="wake-brief-detail"
      className="mt-2 pt-2 border-t border-current/20 text-xs"
    >
      {personaEntries.length === 0 ? (
        <p className="opacity-70">No peer-specific activity to report.</p>
      ) : (
        <ul className="space-y-0.5">
          {personaEntries.map(([personaId, stats]) => (
            <li key={personaId} data-testid={`wake-brief-persona-${personaId}`}>
              <span className="font-medium">{personaId.slice(0, 8)}</span>
              {' '}— {stats.turns} turns, {stats.artifacts_created} artifacts,{' '}
              ${stats.cost_usd}
            </li>
          ))}
        </ul>
      )}
      {digest.highlights.length > 0 && (
        <ul
          data-testid="wake-brief-highlights"
          className="mt-2 space-y-0.5 list-disc list-inside opacity-90"
        >
          {digest.highlights.map((h, i) => (
            <li key={i}>
              <span className="font-medium">{h.kind}</span> by {h.by}: {h.summary}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
